import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { View } from 'react-native'

import { Icon, VFButton, VFText } from '@vfgroup-oneplatform/foundation/Components'

import { Images, useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'

import { CollapsibleCard } from '@vfgroup-oneplatform/framework/CommonUI'

import VFTextWithLoader from '../VFTextWithLoader'

import styles from './Offer.Styles'

import { UhImages, getThemeImages } from '../../Themes'

const getSlotCount = val => {
  const count = val ? val.split(' ')[0] : ''
  return count ? parseInt(count, 10) : 0
}

const Offer = ({
  type,
  offer,
  onPress,
  viewOnly,
  isLoading,
  isSelected,
  stylesArray
}) => {
  const [cardHeight, setCardHeight] = useState(type === 'bundle' ? 100 : 85)

  const theme = useTheme()
  const themedImages = getThemeImages(theme.name)

  const setHeight = event => {
    const { height } = event.nativeEvent.layout
    setCardHeight(height)
  }
  return (
    <div>
      <CollapsibleCard
        id={offer.id}
        key={cardHeight}
        openId={offer.id}
        cardHeight={cardHeight}
        cardStyle={stylesArray}
        renderCard={() => (
          <OfferRecap
            type={type}
            name={offer.name}
            price={offer.price}
            viewOnly={viewOnly}
            onLayout={setHeight}
            isLoading={isLoading}
            themedImages={themedImages}
          />
        )}
        renderCollapseCard={() => {
          if (isLoading) {
            return null
          } else if (type === 'bundle') {
            return (
              <BundleOfferDetails
                viewOnly={viewOnly}
                tv={offer.tv?.name}
                isSelected={isSelected}
                download={offer.download?.value}
                internet={offer.internet?.value}
                onPress={() => onPress(offer.id)}
                tariffPlan={offer.tariffPlan?.value}
                slots={getSlotCount(offer.slots?.value)}
              />
            )
          } else if (type === 'tv') {
            return (
              <TVOfferDetails
                viewOnly={viewOnly}
                isSelected={isSelected}
                themedImages={themedImages}
                onPress={() => onPress(offer.id)}
              />
            )
          } else if (type === 'plan') {
            return (
              <PlanOfferDetails
                viewOnly={viewOnly}
                isSelected={isSelected}
                details={offer.details}
                onPress={() => onPress(offer.id)}
              />
            )
          } else {
            return null
          }
        }}
      />
    </div>
  )
}

const getIcon = (type, themedImages) => {
  switch (type) {
    case 'bundle':
      return themedImages.offer
    case 'plan':
      return themedImages.plan
    case 'tv':
      return themedImages.tv
    default:
      return themedImages.offer
  }
}

const OfferRecap = ({
  type,
  viewOnly,
  name,
  price,
  isLoading,
  onLayout,
  themedImages
}) => {
  const theme = useTheme()
  const icon = getIcon(type, themedImages)
  return (
    <View style={styles.recap} onLayout={onLayout}>
      <Icon size={36} name={icon} type="image" style={styles.offerIcon} />
      <View style={styles.nameHolder}>
        <VFTextWithLoader
          i18nKey={name}
          loaderWidth={160}
          loaderHeight={22}
          numberOfLines={1}
          isLoading={isLoading}
          style={styles.offerTitle}
        />
        {type === 'bundle' ? (
          <View style={styles.price}>
            {viewOnly ? (
              <VFTextWithLoader
                loaderWidth={50}
                loaderHeight={14}
                isLoading={isLoading}
                style={styles.includedText(theme)}
                i18nKey="unlimited_included_in_offer"
              />
            ) : (
              <VFTextWithLoader
                loaderWidth={50}
                loaderHeight={14}
                style={styles.amount}
                isLoading={isLoading}
                placeHolders={[price.value]}
                i18nKey="unlimited_summary_amount"
              />
            )}
          </View>
        ) : null}
      </View>
      <View style={styles.autoMargin}>
        <Icon
          size={24}
          type="image"
          style={styles.chevronIcon}
          name={Images.ic_chevron_down_red}
        />
      </View>
    </View>
  )
}

const extraBenefits = [
  'unlimited_first_benefit',
  'unlimited_second_benefit',
  'unlimited_third_benefit'
]

const BundleOfferDetails = ({
  tv,
  slots,
  onPress,
  download,
  internet,
  viewOnly,
  isSelected,
  tariffPlan
}) => {
  const theme = useTheme()
  const themedImages = getThemeImages(theme.name)

  return (
    <View style={{ flex: 1 }}>
      <View style={styles.benefits(theme)}>
        {/* Broadband */}
        <View style={styles.detailEntry}>
          <Icon
            size={26}
            type="image"
            style={styles.icon}
            name={themedImages.download}
          />
          <View>
            <VFText
              i18nKey={internet}
              style={[styles.text, styles.detailsText, styles.bold]}
            />
            <View style={styles.detailList}>
              <VFText
                i18nKey="unlimited_mbps"
                placeHolders={[`\u2022    ${download}`]}
                style={[styles.text, styles.detailsText]}
              />
            </View>
          </View>
        </View>
        {/* Mobile Plan */}
        <View style={styles.detailEntry}>
          <Icon
            size={26}
            type="image"
            style={styles.icon}
            name={themedImages.mobile}
          />
          <View>
            <VFText
              i18nKey={tariffPlan}
              style={[styles.text, styles.detailsText, styles.bold]}
            />
            <View style={styles.detailList}>
              <VFText
                i18nKey="unlimited_one_admin"
                style={[styles.text, styles.detailsText, styles.spaceBottom]}
              />
              {slots > 0 ? (
                <VFText
                  placeHolders={[slots]}
                  i18nKey="unlimited_slots_count_list"
                  style={[styles.text, styles.detailsText]}
                />
              ) : null}
            </View>
          </View>
        </View>
        {/* TV */}
        {tv ? (
          <View style={styles.detailEntry}>
            <Icon
              size={26}
              type="image"
              style={styles.icon}
              name={themedImages.tv}
            />
            <View>
              <VFText
                placeHolders={[tv]}
                i18nKey="unlimited_tv_name"
                style={[styles.text, styles.detailsText, styles.bold]}
              />
              <View style={styles.detailList}>
                <VFText
                  i18nKey="unlimited_channel_count_list"
                  style={[styles.text, styles.detailsText]}
                />
              </View>
            </View>
          </View>
        ) : null}
      </View>
      {/* More benefits */}
      <View style={styles.buttonHolder}>
        <VFText
          style={[styles.text, styles.moreBenefits]}
          i18nKey="unlimited_more_benefits"
        />
        <View>
          {extraBenefits.map((benefit, i) => (
            <View key={i} style={styles.benefit}>
              <Icon
                size={20}
                type="image"
                style={styles.offerIcon}
                name={UhImages.green_tick}
              />
              <VFText i18nKey={benefit} style={styles.text} />
            </View>
          ))}
        </View>
      </View>
      {/* Bundle Button */}
      {!viewOnly ? (
        <View style={styles.buttonHolder}>
          <VFButton
            title={
              isSelected
                ? 'unlimited_offer_deselect_button'
                : 'unlimited_offer_select_button'
            }
            onPress={onPress}
          />
        </View>
      ) : null}
    </View>
  )
}

const TVOfferDetails = ({ onPress, viewOnly, isSelected, themedImages }) => {
  return (
    <View>
      <View style={styles.benefits}>
        <View style={[styles.detailEntry, styles.alignCenter]}>
          <Icon
            size={26}
            type="image"
            style={styles.icon}
            name={themedImages.tv}
          />
          <VFText
            i18nKey="unlimited_channel_count"
            style={[styles.text, styles.detailsText]}
          />
        </View>
      </View>
      {/* Bundle Button */}
      {!viewOnly ? (
        <View style={styles.buttonHolder}>
          <VFButton
            title={
              isSelected
                ? 'unlimited_offer_deselect_button'
                : 'unlimited_offer_select_button'
            }
            onPress={onPress}
          />
        </View>
      ) : null}
    </View>
  )
}

const PlanOfferDetails = ({ details, onPress, viewOnly, isSelected }) => {
  const theme = useTheme()
  const themedImages = getThemeImages(theme.name)
  return (
    <View>
      <View style={styles.benefits}>
        {/* Internet */}
        <View style={[styles.detailEntry, styles.alignCenter]}>
          <Icon
            size={26}
            type="image"
            style={styles.icon}
            name={themedImages.plan_internet}
          />
          <VFText
            i18nKey="%1$s"
            placeHolders={[details.internet]}
            style={[styles.text, styles.detailsText]}
          />
        </View>
        {/* Minutes */}
        <View style={[styles.detailEntry, styles.alignCenter]}>
          <Icon
            size={26}
            type="image"
            style={styles.icon}
            name={themedImages.plan_minutes}
          />
          <VFText
            i18nKey="unlimited_minutes"
            placeHolders={[details.minutes]}
            style={[styles.text, styles.detailsText]}
          />
        </View>
        {/* SMS */}
        <View style={[styles.detailEntry, styles.alignCenter]}>
          <Icon
            size={26}
            type="image"
            style={styles.icon}
            name={themedImages.plan_messages}
          />
          <VFText
            placeHolders={[details.sms]}
            i18nKey="unlimited_sms_count"
            style={[styles.text, styles.detailsText]}
          />
        </View>
        {/* VCoins */}
        {/* <View style={[styles.detailEntry, styles.alignCenter]}>
          <Icon
            size={26}
            type="image"
            style={styles.icon}
            name={UhImages.tv}
          />
          <VFText
            i18nKey="unlimited_plan_vcoins"
            style={[styles.text, styles.detailsText]}
          />
        </View> */}
        {/* Roaming */}
        {/* <View style={[styles.detailEntry, styles.alignCenter]}>
          <Icon
            size={26}
            type="image"
            style={styles.icon}
            name={UhImages.tv}
          />
          <VFText
            i18nKey="unlimited_plan_roaming"
            style={[styles.text, styles.detailsText]}
          />
        </View> */}
        {/* Contract */}
        {/* <View style={[styles.detailEntry, styles.alignCenter]}>
          <Icon
            size={26}
            type="image"
            style={styles.icon}
            name={UhImages.plan_contract}
          />
          <VFText
            placeHolders={[24]}
            i18nKey="unlimited_plan_period"
            style={[styles.text, styles.detailsText]}
          />
        </View> */}
      </View>
      {/* Bundle Button */}
      {!viewOnly ? (
        <View style={styles.buttonHolder}>
          <VFButton
            title={
              isSelected
                ? 'unlimited_plan_selected_button'
                : 'unlimited_plan_select_button'
            }
            onPress={onPress}
            disabled={isSelected}
          />
        </View>
      ) : null}
    </View>
  )
}

TVOfferDetails.propTypes = {
  onPress: PropTypes.func,
  viewOnly: PropTypes.bool,
  isSelected: PropTypes.bool,
  themedImages: PropTypes.arrayOf(PropTypes.string)
}

PlanOfferDetails.propTypes = {
  onPress: PropTypes.func,
  viewOnly: PropTypes.bool,
  details: PropTypes.number,
  isSelected: PropTypes.bool
}

OfferRecap.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string,
  price: PropTypes.objectOf({
    unit: PropTypes.string,
    value: PropTypes.number
  }),
  viewOnly: PropTypes.bool,
  isLoading: PropTypes.bool,
  onLayout: PropTypes.func,
  themedImages: PropTypes.arrayOf(PropTypes.string)
}

BundleOfferDetails.propTypes = {
  tv: PropTypes.string,
  onPress: PropTypes.func,
  slots: PropTypes.number,
  viewOnly: PropTypes.bool,
  download: PropTypes.string,
  internet: PropTypes.string,
  isSelected: PropTypes.bool,
  tariffPlan: PropTypes.string
}

Offer.propTypes = {
  type: PropTypes.string,
  offer: PropTypes.object,
  onPress: PropTypes.func,
  viewOnly: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSelected: PropTypes.bool,
  stylesArray: PropTypes.arrayOf(PropTypes.string)
}

Offer.defaultProps = {
  offer: {},
  type: 'bundle',
  viewOnly: false,
  stylesArray: [],
  isLoading: false,
  onPress: () => { },
  isSelected: false
}

export default Offer
